import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { FiSearch, FiChevronDown, FiStar, FiClock, FiUsers, FiMapPin, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { translations } from '../utils/homeTranslate';
import { useLanguage } from '../components/LanguajeContext';

const Home = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const navigate = useNavigate();

  // Animaciones
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.6, -0.05, 0.01, 0.99] } }
  };
  const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

  // Helpers
  const URL = process.env.REACT_APP_SERVER_URL;
  const formatCRC = (n) =>
    new Intl.NumberFormat('es-CR', { style: 'currency', currency: 'CRC', maximumFractionDigits: 0 }).format(Number(n) || 0);

  const extractArray = (json) => {
    if (Array.isArray(json)) return json;
    if (Array.isArray(json?.message)) return json.message;
    if (Array.isArray(json?.data)) return json.data;
    if (Array.isArray(json?.results)) return json.results;
    if (Array.isArray(json?.message?.items)) return json.message.items;
    return [];
  };

  const computeStep = (min, max) => {
    const span = Math.max(0, (Number(max) || 0) - (Number(min) || 0));
    if (span <= 1_000) return 10;
    if (span <= 5_000) return 50;
    if (span <= 20_000) return 100;
    if (span <= 100_000) return 500;
    return 1_000;
  };

  // Estados
  const [premiumServices, setPremiumServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeCategory, setActiveCategory] = useState('Todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('featured');

  // Rango dinámico de precios
  const [priceBounds, setPriceBounds] = useState({ min: 0, max: 0 });
  const [priceRange, setPriceRange] = useState([0, 0]);

  const [expandedService, setExpandedService] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Estados de paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [servicesPerPage] = useState(6); // Número de servicios por página

  // Fetch de servicios + normalización
  useEffect(() => {
    let cancelled = false;

    const fetchPremiumServices = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${URL}/api/service/get/all`, {
          method: 'GET',
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache',
            'ngrok-skip-browser-warning': 'true' // <-- AGREGA ESTA LÍNEA
          }
        });

        if (!response.ok) throw new Error(t.errors.loadServices);

        const json = await response.json();

        const rawList = extractArray(json);
        const normalized = rawList.map((s, idx) => {
          const priceNum = Number(s.price ?? s.cost ?? s.amount ?? 0);
          const ratingNum = Number(s.rating ?? 0);

          const categoryStr =
            typeof s.category === 'string'
              ? s.category
              : Array.isArray(s.category)
              ? s.category.join(' / ')
              : Array.isArray(s.categories)
              ? s.categories.join(' / ')
              : (s.categories ?? '');

          return {
            id_service: s.id_service ?? s.id ?? `svc_${idx}`,
            name: s.name ?? s.title ?? 'Servicio',
            description: s.description ?? s.details ?? '',
            category: categoryStr,
            duration: s.duration ?? s.time ?? null,
            max_capacity: s.max_capacity ?? s.capacity ?? null,
            location: s.location ?? s.city ?? null,
            hour_start: s.hour_start ?? s.start ?? null,
            hour_end: s.hour_end ?? s.end ?? null,
            featured: Boolean(s.featured),
            rating: isNaN(ratingNum) ? 0 : ratingNum,
            price: isNaN(priceNum) ? 0 : priceNum,
            activate: s.activate, // por si necesitas filtrar activos
            image:s.url_image?s.url_image:null
          };
        });

        if (cancelled) return;
        setPremiumServices(normalized);

        // calcular bounds y rango inicial en base a la data
        const prices = normalized.map(s => Number(s.price) || 0);
        if (!prices.length) {
          setPriceBounds({ min: 0, max: 0 });
          setPriceRange([0, 0]);
        } else {
          const min = Math.min(...prices);
          const max = Math.max(...prices);
          setPriceBounds({ min, max });
          setPriceRange([min, max]);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (URL) fetchPremiumServices();
    return () => { cancelled = true; };
    //  Dependemos solo de URL para evitar múltiples fetch por cambios de objetos/funciones
  }, [URL, t.errors.loadServices]);

  // Categorías disponibles (derivadas de los datos normalizados)
  const categories = useMemo(() => {
    const cats = new Set(['Todos']);
    premiumServices.forEach(service => {
      if (service.category) {
        service.category.split('/').map(c => c.trim()).filter(Boolean).forEach(c => cats.add(c));
      }
    });
    return Array.from(cats).sort();
  }, [premiumServices]);

  const priceStep = computeStep(priceBounds.min, priceBounds.max);

  // Filtrar + ordenar
  const filteredServices = useMemo(() => {
    const search = (searchTerm ?? '').toLowerCase();

    return premiumServices
      // Opcional: filtrar por activos si tu backend usa ese flag
      .filter(s => (s.activate ? s.activate === 'activate' : true))
      .filter(service => {
        const categoryOk =
          activeCategory === 'Todos' ||
          (service.category && service.category.includes(activeCategory));

        const name = (service.name ?? '').toLowerCase();
        const desc = (service.description ?? '').toLowerCase();
        const searchOk = name.includes(search) || desc.includes(search);

        const price = Number(service.price) || 0;
        const priceOk =
          price >= (priceRange[0] ?? priceBounds.min) &&
          price <= (priceRange[1] ?? priceBounds.max);

        return categoryOk && searchOk && priceOk;
      })
      .sort((a, b) => {
        if (sortBy === 'featured') {
          const feat = (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
          if (feat !== 0) return feat;
          return (Number(b.rating) || 0) - (Number(a.rating) || 0);
        }
        if (sortBy === 'price_asc') return (Number(a.price) || 0) - (Number(b.price) || 0);
        if (sortBy === 'price_desc') return (Number(b.price) || 0) - (Number(a.price) || 0);
        if (sortBy === 'rating') return (Number(b.rating) || 0) - (Number(a.rating) || 0);
        return 0;
      });
  }, [premiumServices, activeCategory, searchTerm, priceRange, priceBounds, sortBy]);

  // Lógica de paginación
  const indexOfLastService = currentPage * servicesPerPage;
  const indexOfFirstService = indexOfLastService - servicesPerPage;
  const currentServices = filteredServices.slice(indexOfFirstService, indexOfLastService);
  const totalPages = Math.ceil(filteredServices.length / servicesPerPage);

  // Cambiar página
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Resetear a primera página cuando cambian los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory, searchTerm, priceRange, sortBy]);

  // Componente de paginación
  const Pagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex justify-center mt-12">
        <nav className="flex items-center space-x-2">
          <button
            onClick={() => paginate(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="p-2 rounded-md border border-[#DAE2E0] text-[#264653] hover:bg-[#FCFCF9] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiChevronLeft className="w-5 h-5" />
          </button>

          {startPage > 1 && (
            <>
              <button
                onClick={() => paginate(1)}
                className={`px-4 py-2 rounded-md border ${currentPage === 1 ? 'border-[#2A9D8F] bg-[#2A9D8F] bg-opacity-10 text-[#2A9D8F]' : 'border-[#DAE2E0] text-[#264653] hover:bg-[#FCFCF9]'}`}
              >
                1
              </button>
              {startPage > 2 && <span className="px-2 text-[#264653] text-opacity-50">...</span>}
            </>
          )}

          {pageNumbers.map(number => (
            <button
              key={number}
              onClick={() => paginate(number)}
              className={`px-4 py-2 rounded-md border ${currentPage === number ? 'border-[#2A9D8F] bg-[#2A9D8F] bg-opacity-10 text-[#2A9D8F]' : 'border-[#DAE2E0] text-[#264653] hover:bg-[#FCFCF9]'}`}
            >
              {number}
            </button>
          ))}

          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && <span className="px-2 text-[#264653] text-opacity-50">...</span>}
              <button
                onClick={() => paginate(totalPages)}
                className={`px-4 py-2 rounded-md border ${currentPage === totalPages ? 'border-[#2A9D8F] bg-[#2A9D8F] bg-opacity-10 text-[#2A9D8F]' : 'border-[#DAE2E0] text-[#264653] hover:bg-[#FCFCF9]'}`}
              >
                {totalPages}
              </button>
            </>
          )}

          <button
            onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="p-2 rounded-md border border-[#DAE2E0] text-[#264653] hover:bg-[#FCFCF9] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiChevronRight className="w-5 h-5" />
          </button>
        </nav>
      </div>
    );
  };

  // Componente de tarjeta de servicio
  const ServiceCard = ({ service }) => (
    <motion.div
      className="bg-white rounded-xl shadow-xl overflow-hidden border border-[#DAE2E0] hover:shadow-2xl transition-all duration-300"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
       <div className="relative h-64">
      {/* Mostrar imagen si existe, sino mostrar gradiente */}
      {service.image ? (
        <img 
          src={service.image} 
          alt={service.name}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-r from-[#2A9D8F] to-[#E9C46A] flex items-center justify-center text-white">
          <span className="text-xl font-light tracking-wider">{service.name}</span>
        </div>
      )}
      
      {service.featured && (
        <div className="absolute top-4 left-4 bg-[#F4A261] text-white px-3 py-1 rounded-full text-xs font-semibold">
          {t.featured}
        </div>
      )}
    </div>

      <div className="p-6">
        <div className="flex justify-between items-start mb-3">
          <h3 className="text-xl font-semibold text-[#264653]">{service.name}</h3>
          {Number(service.rating) > 0 && (
            <div className="flex items-center bg-[#2A9D8F] bg-opacity-10 px-2 py-1 rounded">
              <FiStar className="text-[#E9C46A] mr-1" />
              <span className="text-sm font-medium text-[#2A9D8F]">{service.rating}</span>
            </div>
          )}
        </div>

        {service.description && (
          <p className="text-[#264653] text-opacity-80 mb-4">{service.description}</p>
        )}

        <div className="grid grid-cols-2 gap-3 mb-4">
          {service.duration && (
            <div className="flex items-center text-sm text-[#264653] text-opacity-70">
              <FiClock className="mr-2" />
              {service.duration}
            </div>
          )}
          {service.max_capacity && (
            <div className="flex items-center text-sm text-[#264653] text-opacity-70">
              <FiUsers className="mr-2" />
              {service.max_capacity}
            </div>
          )}
          {service.location && (
            <div className="flex items-center text-sm text-[#264653] text-opacity-70">
              <FiMapPin className="mr-2" />
              {service.location}
            </div>
          )}
          <div className="text-right">
            <span className="text-2xl font-bold text-[#264653]">{formatCRC(service.price)}</span>
            <span className="text-[#264653] text-opacity-70 text-sm ml-1">{t.pricePerPerson}</span>
          </div>
        </div>

        <div className="flex justify-between items-center">
          <button
            onClick={() => navigate(`/reservation/${service.id_service}`)}
            className="bg-[#2A9D8F] hover:bg-[#21867a] text-white px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-md hover:shadow-lg"
          >
            {t.bookNow}
          </button>
        </div>

        <AnimatePresence>
          {expandedService === service.id_service && (service.hour_end || service.hour_start) && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="mt-4 pt-4 border-t border-[#DAE2E0]">
                <h4 className="font-medium text-[#264653] mb-2">{t.includes}:</h4>
                <ul className="grid grid-cols-2 gap-2">
                  {service.hour_start && (
                    <li className="flex items-center text-sm text-[#264653] text-opacity-80">
                      <span className="w-1.5 h-1.5 bg-[#2A9D8F] rounded-full mr-2"></span>
                      {service.hour_start}
                    </li>
                  )}
                  {service.hour_end && (
                    <li className="flex items-center text-sm text-[#264653] text-opacity-80">
                      <span className="w-1.5 h-1.5 bg-[#2A9D8F] rounded-full mr-2"></span>
                      {service.hour_end}
                    </li>
                  )}
                </ul>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );

  // Estados de carga
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCFCF9]">
        <Navbar />
        <div className="container mx-auto px-6 py-24 text-center">
          <div className="animate-pulse flex flex-col items-center">
            <div className="h-12 w-12 bg-[#2A9D8F] bg-opacity-20 rounded-full mb-4"></div>
            <div className="h-4 bg-[#2A9D8F] bg-opacity-20 rounded w-1/4 mb-2"></div>
            <div className="h-4 bg-[#2A9D8F] bg-opacity-20 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  // Manejo de errores
  if (error) {
    return (
      <div className="min-h-screen bg-[#FCFCF9]">
        <Navbar />
        <div className="container mx-auto px-6 py-24 text-center">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded max-w-md mx-auto">
            <p>{t.errors.loadServices}: {error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm font-medium"
            >
              {t.tryAgain}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render principal
  return (
    <div className="min-h-screen bg-[#FCFCF9]">
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-[#2A9D8F] to-[#E9C46A] text-white py-24 overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        {/* Elementos decorativos orgánicos - MÁS CÍRCULOS */}
        <div className="absolute top-10 right-10 w-32 h-32 bg-[#45B69C] rounded-full mix-blend-soft-light opacity-20"></div>
        <div className="absolute bottom-10 left-10 w-24 h-24 bg-[#E9C46A] rounded-full mix-blend-soft-light opacity-30"></div>
        <div className="absolute top-1/4 left-1/4 w-16 h-16 bg-[#89B98A] rounded-full mix-blend-soft-light opacity-25"></div>
        <div className="absolute bottom-1/3 right-1/3 w-20 h-20 bg-[#F4A261] rounded-full mix-blend-soft-light opacity-20"></div>
        <div className="absolute top-3/4 right-1/4 w-12 h-12 bg-[#2A9D8F] rounded-full mix-blend-soft-light opacity-30"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div variants={fadeInUp} initial="hidden" animate="visible">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              {t.heroTitle}
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-2xl">
              {t.heroSubtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => window.location.href = "https://www.travelwithpiawakcr.com/contact-5"}
                className="bg-transparent border-2 border-white hover:bg-white hover:bg-opacity-10 px-8 py-3 rounded-lg text-lg font-semibold transition-all"
              >
                {t.contactUs}
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Filtros Premium */}
      <section className="py-12 bg-white shadow-sm relative overflow-hidden">
        {/* Círculos en sección de filtros */}
        <div className="absolute -top-8 -left-8 w-24 h-24 bg-[#E9C46A] rounded-full mix-blend-soft-light opacity-15"></div>
        <div className="absolute -bottom-12 -right-12 w-32 h-32 bg-[#2A9D8F] rounded-full mix-blend-soft-light opacity-10"></div>
        <div className="absolute top-1/2 left-1/3 w-16 h-16 bg-[#89B98A] rounded-full mix-blend-soft-light opacity-10"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div className="bg-white rounded-xl shadow-md p-6" variants={fadeInUp}>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
              <h2 className="text-2xl font-bold text-[#264653] mb-4 md:mb-0">
                {t.ourExperiences}
                <span className="text-[#264653] text-opacity-60 font-normal ml-2">({filteredServices.length})</span>
              </h2>

              <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
                <div className="relative flex-grow">
                  <FiSearch className="absolute left-3 top-3.5 text-[#264653] text-opacity-40" />
                  <input
                    type="text"
                    placeholder={t.searchPlaceholder}
                    className="w-full pl-10 pr-4 py-2.5 border border-[#DAE2E0] rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F]"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center justify-center px-4 py-2.5 border border-[#DAE2E0] rounded-lg hover:bg-[#FCFCF9] transition-colors"
                >
                  <span>{t.filters}</span>
                  <FiChevronDown className={`ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                </button>
              </div>
            </div>

            {/* Panel de Filtros Avanzados */}
            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="grid md:grid-cols-3 gap-6 mt-6 pt-6 border-t border-[#DAE2E0]">
                    {/* Filtro por Categoría */}
                    <div>
                      <label className="block text-sm font-medium text-[#264653] mb-2">{t.category}</label>
                      <select
                        className="w-full pl-3 pr-10 py-2.5 border border-[#DAE2E0] rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F]"
                        value={activeCategory}
                        onChange={(e) => setActiveCategory(e.target.value)}
                      >
                        {categories.map(category => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                    </div>

                    {/* Filtro por Precio (dinámico) */}
                    <div>
                      <label className="block text-sm font-medium text-[#264653] mb-2">
                        {t.priceRange
                          .replace('${min}', formatCRC(priceRange[0] ?? priceBounds.min))
                          .replace('${max}', formatCRC(priceRange[1] ?? priceBounds.max))
                        }
                      </label>
                      <div className="px-2">
                        <input
                          type="range"
                          min={priceBounds.min}
                          max={priceBounds.max}
                          step={priceStep}
                          value={priceRange[0]}
                          onChange={(e) => {
                            const v = Number(e.target.value);
                            setPriceRange([Math.min(v, priceRange[1]), priceRange[1]]);
                          }}
                          className="w-full mb-2"
                          disabled={priceBounds.min === priceBounds.max}
                        />
                        <input
                          type="range"
                          min={priceBounds.min}
                          max={priceBounds.max}
                          step={priceStep}
                          value={priceRange[1]}
                          onChange={(e) => {
                            const v = Number(e.target.value);
                            setPriceRange([priceRange[0], Math.max(v, priceRange[0])]);
                          }}
                          className="w-full"
                          disabled={priceBounds.min === priceBounds.max}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#264653] mb-2">{t.sortBy}</label>
                      <select
                        className="w-full pl-3 pr-10 py-2.5 border border-[#DAE2E0] rounded-lg focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F]"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                      >
                        <option value="price_asc">{t.sortOptions.price_asc}</option>
                        <option value="price_desc">{t.sortOptions.price_desc}</option>
                      </select>
                    </div>
                  </div>

                  <div className="mt-6">
                    <button
                      onClick={() => {
                        setActiveCategory('Todos');
                        setSearchTerm('');
                        setSortBy('featured');
                        setPriceRange([priceBounds.min, priceBounds.max]); // volver a los bounds reales
                      }}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#2A9D8F] hover:bg-[#21867a]"
                    >
                      {t.resetFilters}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* Listado de Servicios */}
      <section className="py-16 bg-[#FCFCF9] relative overflow-hidden">
        {/* Círculos en sección de servicios */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-[#45B69C] rounded-full mix-blend-soft-light opacity-15"></div>
        <div className="absolute bottom-40 right-20 w-28 h-28 bg-[#E9C46A] rounded-full mix-blend-soft-light opacity-12"></div>
        <div className="absolute top-1/2 left-1/2 w-24 h-24 bg-[#89B98A] rounded-full mix-blend-soft-light opacity-10 transform -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute top-1/3 right-40 w-16 h-16 bg-[#F4A261] rounded-full mix-blend-soft-light opacity-15"></div>
        <div className="absolute bottom-20 left-40 w-18 h-18 bg-[#2A9D8F] rounded-full mix-blend-soft-light opacity-10"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          {filteredServices.length === 0 ? (
            <motion.div
              className="text-center py-20"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <div className="max-w-md mx-auto">
                <FiSearch className="mx-auto h-12 w-12 text-[#264653] text-opacity-40 mb-4" />
                <h3 className="text-lg font-medium text-[#264653] mb-2">{t.noResultsTitle}</h3>
                <p className="text-[#264653] text-opacity-70 mb-6">
                  {t.noResultsMessage}
                </p>
                <button
                  onClick={() => {
                    setActiveCategory('Todos');
                    setSearchTerm('');
                    setPriceRange([priceBounds.min, priceBounds.max]);
                    setSortBy('featured');
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#2A9D8F] hover:bg-[#21867a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2A9D8F]"
                >
                  {t.resetFilters}
                </button>
              </div>
            </motion.div>
          ) : (
            <>
              <motion.div
                className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
                variants={stagger}
                initial="hidden"
                animate="visible"
              >
                {currentServices.map(service => (
                  <ServiceCard key={service.id_service} service={service} />
                ))}
              </motion.div>
              
              {/* Paginación */}
              <Pagination />
              
              {/* Información de paginación */}
              <div className="mt-4 text-center text-[#264653] text-opacity-70 text-sm">
                {t.showing} {indexOfFirstService + 1}-{Math.min(indexOfLastService, filteredServices.length)} {t.of} {filteredServices.length} {t.services}
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#2A9D8F] to-[#E9C46A] text-white relative overflow-hidden">
        {/* Elementos decorativos orgánicos - MÁS CÍRCULOS */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-[#45B69C] rounded-full mix-blend-soft-light opacity-20 -translate-x-32 -translate-y-32"></div>
        <div className="absolute bottom-0 right-0 w-48 h-48 bg-[#E9C46A] rounded-full mix-blend-soft-light opacity-30 translate-x-24 translate-y-24"></div>
        <div className="absolute top-1/3 left-1/4 w-32 h-32 bg-[#89B98A] rounded-full mix-blend-soft-light opacity-25"></div>
        <div className="absolute bottom-1/4 right-1/3 w-28 h-28 bg-[#F4A261] rounded-full mix-blend-soft-light opacity-20"></div>
        <div className="absolute top-1/2 right-1/4 w-20 h-20 bg-[#2A9D8F] rounded-full mix-blend-soft-light opacity-30"></div>
        <div className="absolute bottom-1/3 left-1/3 w-24 h-24 bg-[#45B69C] rounded-full mix-blend-soft-light opacity-15"></div>
        
        <div className="container mx-auto px-6 text-center relative z-10">
          <motion.div variants={fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">{t.providerTitle}</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              {t.providerSubtitle}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <button
                onClick={() => window.location.href = 'https://www.travelwithpiawakcr.com/pricing-plans/list'}
                className="bg-white text-[#2A9D8F] hover:bg-[#FCFCF9] px-8 py-3 rounded-lg text-lg font-semibold transition-all shadow-lg hover:shadow-xl"
              >
                {t.registerProvider}
              </button>
              <button
                onClick={() =>  window.location.href = "https://www.travelwithpiawakcr.com/contact-5"}
                className="bg-transparent border-2 border-white hover:bg-white hover:bg-opacity-10 px-8 py-3 rounded-lg text-lg font-semibold transition-all"
              >
                {t.requestInfo}
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      <footer className="bg-[#2A9D8F] text-[#FCFCF9] pt-16 pb-8 relative overflow-hidden">
        {/* Círculos en el footer */}
        <div className="absolute top-10 right-20 w-20 h-20 bg-[#45B69C] rounded-full mix-blend-soft-light opacity-15"></div>
        <div className="absolute bottom-20 left-32 w-24 h-24 bg-[#E9C46A] rounded-full mix-blend-soft-light opacity-10"></div>
        <div className="absolute top-1/2 right-40 w-16 h-16 bg-[#89B98A] rounded-full mix-blend-soft-light opacity-12"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            <div>
              <h3 className="text-white text-lg font-semibold mb-6"></h3>
              <p className="mb-6">
                {t.footerDescription}
              </p>
              <div className="flex space-x-4"></div>
            </div>

            <div>
              <h3 className="text-white text-lg font-semibold mb-6">{t.company}</h3>
              <ul className="space-y-3">
                <li><button onClick={() => navigate('/nosotros')} className="hover:text-white transition-colors">{t.ourPhilosophy}</button></li>
              </ul>
            </div>

            <div>
              <h3 className="text-white text-lg font-semibold mb-6">{t.assistance}</h3>
              <ul className="space-y-3">
                <li><button onClick={() => window.location.href='https://www.travelwithpiawakcr.com/contact-5'} className="hover:text-white transition-colors">{t.contactUs}</button></li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-[#45B69C] text-sm text-center">
            <p>© {new Date().getFullYear()} {t.madeIn}.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;